import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountTree from '@mui/icons-material/AccountTree';
import Article from '@mui/icons-material/Article';
import MoreVert from '@mui/icons-material/MoreVert';
import OpenInNew from '@mui/icons-material/OpenInNew';
import School from '@mui/icons-material/School';

import type {
  SharedCurriculumFilter,
  SharedCurriculumItem,
  SharedCurriculumObjectType,
} from '@/shared/types/sharedCurriculum';
import UserAvatar from '@/shared/components/UserAvatar';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { formatRelativeTime } from '@/shared/utils/dateUtils';

const FILTERS: Array<{ value: SharedCurriculumFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'course', label: 'Courses' },
  { value: 'unit', label: 'Units' },
  { value: 'lesson', label: 'Lessons' },
];

const TYPE_LABELS: Record<SharedCurriculumObjectType, string> = {
  course: 'Course',
  unit: 'Unit',
  lesson: 'Lesson',
};

const PERMISSION_LABELS = {
  viewer: 'Viewer',
  editor: 'Editor',
};

type SharedWithMeListProps = {
  items: SharedCurriculumItem[];
  isLoading: boolean;
  error: unknown;
  onOpenItem: (item: SharedCurriculumItem) => void;
};

function typeIcon(type: SharedCurriculumObjectType) {
  if (type === 'course') return <School fontSize="small" />;
  if (type === 'unit') return <AccountTree fontSize="small" />;
  return <Article fontSize="small" />;
}

function parentContext(item: SharedCurriculumItem): string {
  if (item.object_type === 'course') return '';
  if (item.object_type === 'unit') {
    return item.has_course_access ? (item.course_title ?? '') : '';
  }
  // Lesson: show accessible parent names only
  const parts: string[] = [];
  if (item.has_course_access && item.course_title) parts.push(item.course_title);
  if (item.has_unit_access && item.unit_title) parts.push(item.unit_title);
  return parts.join(' / ');
}

export default function SharedWithMeList({
  items,
  isLoading,
  error,
  onOpenItem,
}: SharedWithMeListProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filter, setFilter] = useState<SharedCurriculumFilter>('all');
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedActionItem, setSelectedActionItem] = useState<SharedCurriculumItem | null>(null);
  const actionMenuOpen = Boolean(actionMenuAnchorEl);

  const filteredItems = useMemo(
    () => (filter === 'all' ? items : items.filter((item) => item.object_type === filter)),
    [filter, items]
  );

  const handleActionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    item: SharedCurriculumItem
  ) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedActionItem(item);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setSelectedActionItem(null);
  };

  const handleOpenSelectedItem = () => {
    const item = selectedActionItem;
    if (!item) return;
    handleActionMenuClose();
    onOpenItem(item);
  };

  if (isLoading) {
    return <PageSpinner />;
  }

  if (error) {
    console.error('Shared curriculum loading error:', error);
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">Failed to load shared curriculum. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {FILTERS.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            color={filter === option.value ? 'primary' : 'default'}
            variant={filter === option.value ? 'filled' : 'outlined'}
            onClick={() => setFilter(option.value)}
          />
        ))}
      </Box>

      {items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No shared curriculum found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No curriculum has been shared with you yet.
          </Typography>
        </Box>
      ) : filteredItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No matching shared curriculum
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try a different type filter.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell>Name</TableCell>
                {!isMobile && <TableCell>Description</TableCell>}
                {!isMobile && <TableCell>Owner</TableCell>}
                {!isMobile && <TableCell>Role</TableCell>}
                {!isMobile && <TableCell>Shared</TableCell>}
                {!isMobile && <TableCell>Last Modified</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow
                  key={`${item.object_type}:${item.object_id}`}
                  hover
                  role="button"
                  tabIndex={0}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onOpenItem(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenItem(item);
                    }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                        {typeIcon(item.object_type)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="medium" noWrap>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {TYPE_LABELS[item.object_type]}
                          {isMobile ? ` - ${parentContext(item)}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography
                        variant="body2"
                        color={item.description ? 'text.secondary' : 'text.disabled'}
                        noWrap
                        sx={{ fontStyle: item.description ? 'normal' : 'italic' }}
                      >
                        {item.description || 'No description'}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Tooltip title={`Created by ${item.owner_name ?? 'Unknown owner'}`}>
                        <UserAvatar
                          userId={item.owner_id}
                          name={item.owner_name ?? undefined}
                          src={item.owner_avatar_url}
                          size={28}
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Chip
                        label={PERMISSION_LABELS[item.permission_type]}
                        color={item.permission_type === 'editor' ? 'primary' : 'default'}
                        variant={item.permission_type === 'editor' ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(item.shared_at)}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(item.updated_at)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${item.title}`}
                        aria-controls={
                          actionMenuOpen ? 'shared-curriculum-actions-menu' : undefined
                        }
                        aria-haspopup="menu"
                        aria-expanded={actionMenuOpen ? 'true' : undefined}
                        onClick={(event) => {
                          handleActionMenuOpen(event, item);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Menu
            id="shared-curriculum-actions-menu"
            anchorEl={actionMenuAnchorEl}
            open={actionMenuOpen}
            onClose={handleActionMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleOpenSelectedItem} disabled={!selectedActionItem}>
              <OpenInNew fontSize="small" sx={{ mr: 1 }} />
              Open
            </MenuItem>
          </Menu>
        </TableContainer>
      )}
    </Box>
  );
}
